import React, {useCallback, useEffect, useRef, useState} from 'react';
import paper from 'paper';
import { useTranslation } from "@/lib/i18n";
import {usePaperSetup} from './use-paper-setup';
import {useCanvasViewport} from './use-canvas-viewport';
import {useBackgroundCanvas} from './use-background-canvas';
import {useRulers} from './use-rulers';
import {useTransformOverlay} from './use-transform-overlay';
import {useGuideSnapping} from './use-guide-snapping';
import {useTextTool} from './use-text-tool';
import {useCanvasInteractions} from './use-canvas-interactions';

import CanvasToolIndicator from './canvas-tool-indicator';
import CanvasTopPanel from './canvas-top-panel';
import {forwardRef, useImperativeHandle} from 'react';

export {clearSelection, drawGrid} from './paper-system-utils';

const Canvas = forwardRef(({
  activeTool,
  onProjectChange,
  setActiveTool,
  customGuides,
  setCustomGuides,
  theme = 'dark',
  showGrid,
  setShowGrid,
  showCrosshair,
  setShowCrosshair,
  snapToGuides,
  setSnapToGuides,
  glassType,
  setGlassType,
  viewMode,
}, ref) => {
  const { t } = useTranslation('editor');
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  useImperativeHandle(ref, () => ({
    centerView
  }));

  const [editingText, setEditingText] = useState(null);

  const {calculateSnapDelta, resetGuidesHighlight, activeSnap} = useGuideSnapping({
    snapToGuides,
    customGuides,
  });

  const {backgroundCanvasRef, drawBackgroundCanvas} = useBackgroundCanvas({
    showGrid,
    glassType,
    showCrosshair,
    activeSnap,
    customGuides,
    theme,
  });

  const {
    rulersCanvasRef,
    drawRulers,
    handleRulerMouseDown,
    handleRulerMouseMove,
    handleRulerMouseUp,
    handleMouseLeave,
  } = useRulers({
    onProjectChange,
    showCrosshair,
    setCustomGuides,
    theme,
  });

  const updateTransformUI = useTransformOverlay(theme);

  const addTextAsPath = useTextTool({setActiveTool, onProjectChange, updateTransformUI, viewMode});

  usePaperSetup({
    canvasRef,
    rulersCanvasRef,
    backgroundCanvasRef,
    drawBackgroundCanvas,
    drawRulers,
    updateTransformUI,
    onProjectChange,
  });

  const {isPanning} = useCanvasInteractions({
    activeTool,
    setActiveTool,
    onProjectChange,
    drawBackgroundCanvas,
    drawRulers,
    updateTransformUI,
    calculateSnapDelta,
    resetGuidesHighlight,
    addTextAsPath,
    customGuides,
    setCustomGuides,
    viewMode,
    t,
    theme,
  });

  const {zoomLevel, centerView, resetZoom} = useCanvasViewport({
    containerRef,
    drawBackgroundCanvas,
    drawRulers,
    updateTransformUI,
  });

  useEffect(() => {
    if (!paper.project) return;
    
    const isVector = viewMode === 'vector';
    const mainLayer = paper.project.layers['mainLayer'] || paper.project.activeLayer;
    if (!mainLayer) return;

    const applyVectorStyles = (item) => {
      if (item.data?.isSystem || item.data?.isTransformHandle || (item instanceof paper.Layer)) return;

      if (isVector) {
        if (!item.data?.origStyle) {
          const type = item.data?.type;
          const isText = item instanceof paper.PointText;
          
          if (type || isText) {
            const isLineOrArrow = type === 'line' || type === 'arrow';
            const isArrowHead = item.closed && type === 'arrow';
            
            item.data.origStyle = {
              fillColor: isText ? (item.fillColor ? item.fillColor.toCSS(true) : '#ef4444') : (isLineOrArrow ? (isArrowHead ? '#000000' : null) : (item.fillColor ? item.fillColor.toCSS(true) : '#d59029')),
              strokeColor: item.strokeColor ? item.strokeColor.toCSS(true) : (isText ? '#ef4444' : '#000000'),
              strokeWidth: item.strokeWidth || (isText ? 1.5 : 1),
              strokeScaling: item.strokeScaling !== undefined ? item.strokeScaling : (isText ? true : false)
            };
          } else {
            item.data.origStyle = {
              fillColor: item.fillColor ? (item.fillColor.alpha === 0 ? 'transparent' : item.fillColor.toCSS(true)) : null,
              strokeColor: item.strokeColor ? item.strokeColor.toCSS(true) : null,
              strokeWidth: item.strokeWidth,
              strokeScaling: item.strokeScaling !== undefined ? item.strokeScaling : true
            };
          }
        }
        
        if (item instanceof paper.Group) {
          item.children.forEach(applyVectorStyles);
          return;
        }

        item.fillColor = new paper.Color(0, 0, 0, 0.001);

        if (item.data?.isOutline) {
          item.strokeColor = item.data.origStyle?.strokeColor || (theme === 'dark' ? '#d4d4d8' : '#3f3f46');
          item.strokeWidth = 1 / paper.view.zoom;
          item.strokeScaling = false;
        } else if (item.data?.origStyle && item.data.origStyle.strokeWidth > 0 && item.data.origStyle.strokeColor) {
           item.strokeColor = item.data.origStyle.strokeColor;
           item.strokeWidth = item.data.origStyle.strokeWidth;
           item.strokeScaling = item.data.origStyle.strokeScaling;
        } else {
           item.strokeColor = theme === 'dark' ? '#d4d4d8' : '#3f3f46';
           item.strokeWidth = 1 / paper.view.zoom;
           item.strokeScaling = false;
        }
      } else {
        if (item.data?.origStyle) {
          const { fillColor, strokeColor, strokeWidth, strokeScaling } = item.data.origStyle;
          
          if (item instanceof paper.Group) {
            item.children.forEach(applyVectorStyles);
          } else {
            item.fillColor = (fillColor === 'transparent') ? new paper.Color(0, 0, 0, 0) : fillColor;
            item.strokeColor = strokeColor;
            item.strokeWidth = strokeWidth;
            item.strokeScaling = strokeScaling !== undefined ? strokeScaling : true;
          }
        }
      }
    };

    mainLayer.getItems({ recursive: true }).forEach(applyVectorStyles);
    
    paper.view.draw();
  }, [viewMode, theme]);

  useEffect(() => {
    const handleEditEvent = (e) => {
      const item = e.detail.item;
      if (!item) return;
      item.visible = false;
      paper.view.update();
      
      const viewPos = paper.view.projectToView(item.bounds.topLeft);
      const zoom = paper.view.zoom;
      
      setEditingText({
        item,
        value: item.content,
        position: { x: viewPos.x, y: viewPos.y },
        size: {
          width: (item.bounds.width * zoom) + 20,
          height: (item.bounds.height * zoom) + 10
        }
      });
    };

    window.addEventListener('edit-text-item', handleEditEvent);
    return () => {
      window.removeEventListener('edit-text-item', handleEditEvent);
    };
  }, []);

  const handleInputBlur = useCallback((e) => {
    if (editingText) {
      const { item } = editingText;
      const value = e ? e.target.value : editingText.value;
      
      item.content = value;
      item.visible = true;

      if (!value || value.trim() === '') {
        item.remove();
      } else {
        item.selected = true;
        
        const cleanText = value.trim()
          .toLowerCase()
          .replace(/[^a-zа-я0-9ієїґ\s\n]/g, '')
          .replace(/[\s\n]+/g, '_');
        item.name = `text_${cleanText}`;
      }
      
      setEditingText(null);
      paper.view.update();
      onProjectChange?.();
      updateTransformUI();
    }
  }, [editingText, onProjectChange, updateTransformUI]);

  const handleOutsideClick = useCallback((e) => {
    if (e.target.tagName !== 'TEXTAREA' || !e.target.classList.contains('text-editing-input')) {
      const input = document.querySelector('.text-editing-input');
      const fakeEvent = input ? { target: input } : null;
      handleInputBlur(fakeEvent);
    }
  }, [handleInputBlur]);

  useEffect(() => {
    if (editingText) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleOutsideClick);
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }
  }, [editingText, handleOutsideClick]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const input = e.target;
    
    setEditingText(prev => {
      if (prev && prev.item) {
        prev.item.content = newValue;
        
        input.style.width = 'auto';
        input.style.height = 'auto';
        const newWidth = Math.max(20, input.scrollWidth);
        const newHeight = input.scrollHeight;
        const viewPos = paper.view.projectToView(prev.item.bounds.topLeft);
        
        return { 
          ...prev, 
          value: newValue, 
          position: { x: viewPos.x, y: viewPos.y },
          size: { width: newWidth, height: newHeight }
        };
      }
      return prev;
    });
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && e.shiftKey) {
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      const { item } = editingText;
      if (item) {
        item.visible = true;
        if (!item.content || item.content.trim() === '') {
          item.remove();
        }
      }
      setEditingText(null);
      paper.view.update();
      updateTransformUI();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`flex-1 border rounded-lg overflow-hidden flex justify-center items-center relative min-h-[500px] select-none transition-colors duration-300 ${
        theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-slate-50 border-slate-200 shadow-inner'
      }`}
      onPointerMove={handleRulerMouseMove}
      onPointerLeave={handleMouseLeave}
      onPointerDown={handleRulerMouseDown}
      onPointerUp={handleRulerMouseUp}
    >
      <CanvasToolIndicator activeTool={activeTool} theme={theme}/>

      <CanvasTopPanel
        glassType={glassType}
        setGlassType={setGlassType}
        showGrid={showGrid}
        setShowGrid={setShowGrid}
        showCrosshair={showCrosshair}
        setShowCrosshair={setShowCrosshair}
        snapToGuides={snapToGuides}
        setSnapToGuides={setSnapToGuides}
        onCenterView={centerView}
        zoomLevel={zoomLevel}
        onResetZoom={resetZoom}
        theme={theme}
      />
      <canvas ref={backgroundCanvasRef} className="absolute inset-0 z-0 pointer-events-none"/>
      <canvas
        ref={canvasRef}
        className={`relative z-10 w-full h-full ${
          isPanning
            ? '!cursor-grabbing'
            : activeTool === 'hand'
              ? 'cursor-grab active:cursor-grabbing'
              : 'cursor-crosshair'
        }`}
        style={{ touchAction: 'none' }}
      />

      {editingText && (
        <textarea
          autoFocus
          className="absolute outline-none bg-transparent border-none p-0 m-0 leading-none whitespace-pre overflow-hidden resize-none text-editing-input"
          style={{
            left: editingText.position.x,
            top: editingText.position.y,
            transform: `scale(${paper.view.zoom}) skewX(${editingText.item.data?.isItalic ? -11 : 0}deg)`,
            transformOrigin: 'left top',
            fontSize: `${editingText.item.fontSize}px`,
            fontFamily: editingText.item.fontFamily,
            fontWeight: editingText.item.fontWeight,
            fontStyle: editingText.item.fontStyle,
            textAlign: editingText.item.justification || 'left',
            color: editingText.item.fillColor.toCSS(),
            caretColor: editingText.item.fillColor.toCSS(),
            width: editingText.size?.width ? `${editingText.size.width / paper.view.zoom}px` : 'auto',
            height: editingText.size?.height ? `${editingText.size.height / paper.view.zoom}px` : 'auto',
            minWidth: '20px',
            zIndex: 50,
            pointerEvents: 'auto',
            display: 'block',
          }}
          value={editingText.value}
          onChange={handleInputChange}
          onBlur={(e) => handleInputBlur(e)}
          onKeyDown={handleInputKeyDown}
        />
      )}

      <canvas ref={rulersCanvasRef} className="absolute inset-0 z-20 pointer-events-none"/>
    </div>
  );
});

export default Canvas;